import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';

import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { EmailService } from '../../services/email.service';
import { PhoneService } from '../../services/phone.service';
import { ModalService } from '../modal.service';
import { ToastService } from '../toast/toast.service';
import { Store } from '@ngrx/store';
import { PolicyState } from '../../store/policy/policy.reducer';
import { take } from 'rxjs/operators';

interface PdfDialogData {
    pdfUrl: string;
    fileName: string;
    documentCategory: string;
    attachmentDocId?: string;
    policyNo?: string;
    attachmentDocType?: string;
    subCategory?: string;
    commType?: 'in' | 'out';
}

@Component({
    selector: 'app-pdf-viewer',
    imports: [MatDialogModule, MatIconModule, ...PSIC_COMPONENTS, DragDropModule],
    templateUrl: './pdf-viewer.component.html',
    styleUrls: ['./pdf-viewer.component.scss'],
})
export class PdfViewerComponent {
    sanitizedUrl: SafeResourceUrl;
    private phoneService = inject(PhoneService);

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PdfDialogData,
        private dialogRef: MatDialogRef<PdfViewerComponent>,
        private sanitizer: DomSanitizer,
        private emailService: EmailService,
        private modalService: ModalService,
        private toastService: ToastService,
        private store: Store<{ policy: PolicyState }>
    ) {
        this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.pdfUrl);
    }

    openInNewWindow() {
        window.open(this.data.pdfUrl, '_blank');
        this.dialogRef.close();
    }

    onClose() {
        this.dialogRef.close();
    }

    sendDocumentByEmail() {
        this.store
            .select((state) => state.policy.data)
            .pipe(take(1))
            .subscribe((policyData) => {
                const insuredInformation =
                    policyData?.[this.data.policyNo || '']?.policySnapshot?.contactPreferences?.insuredInformation;

                const popup = {
                    type: 'actionDialog',
                    emailVerified: false,
                    messageTitle: 'Send file by email',
                    messageSubtitle: 'Select the channel through which the link will be sent.',
                    imageSrc: 'assets/images/send-document.svg',
                    imageWidth: '120px',
                    imageHeight: '120px',
                    confirm: 'Send',
                    cancel: 'Cancel',
                    fields: [
                        {
                            type: 'text',
                            caption: 'Email',
                            name: 'email',
                            id: 'email',
                            placeholder: 'Enter email address',
                            required: true,
                            keyboardFilter: 'email',
                            value: '',
                        },
                        {
                            type: 'button',
                            caption: 'Verify Email',
                            name: 'verifyEmail',
                            buttonType: 'borderless',
                            imageSrc: '',
                        },
                    ],
                };

                this.modalService.confirm(popup, 'send-via-modal-above-pdf', insuredInformation).subscribe({
                    next: (popupCallbackDetail) => {
                        if (popupCallbackDetail?.[0] === 'Send') {
                            this.sendEmail(popupCallbackDetail[1]);
                        }
                    },
                    error: () =>
                        this.showToast('error', 'Email Error', 'Unable to open email dialog. Please try again.'),
                });
            });
    }

    private sendEmail(recipientEmail: string | any) {
        const email = typeof recipientEmail === 'object' ? recipientEmail?.email : recipientEmail;
        if (!this.emailService.validateEmailFormat(email)) {
            this.showToast('error', 'Invalid Email', 'Please enter a valid email address.');
            return;
        }

        const doc = {
            fileName: this.data.fileName,
            attachmentDocId: this.data.attachmentDocId || '',
        };

        // Close the viewer immediately as the loader appears; the send continues in the background.
        this.dialogRef.close();

        this.emailService
            .sendDocumentEmail(this.emailService.buildMultipleDocumentEmailRequest([doc], email))
            .subscribe({
                next: () => this.showToast('success', '', `The ${this.data.documentCategory} was sent successfully.`),
                error: () => this.showToast('error', '', `The ${this.data.documentCategory} couldn't be sent.`),
            });
    }

    private showToast(type: 'success' | 'error', title: string, message: string) {
        this.toastService.add({ type, title, message });
    }

    sendDocumentBySMS() {
        this.store
            .select((state) => state.policy.data)
            .pipe(take(1))
            .subscribe((policyData) => {
                const insuredInformation =
                    policyData?.[this.data.policyNo || '']?.policySnapshot?.contactPreferences?.insuredInformation;

                const popup = {
                    type: 'actionDialog',
                    emailVerified: false,
                    messageTitle: 'Send file by SMS',
                    messageSubtitle: 'Select the channel through which the link will be sent.',
                    imageSrc: 'assets/images/send-document.svg',
                    imageWidth: '120px',
                    imageHeight: '120px',
                    confirm: 'Send',
                    cancel: 'Cancel',
                    fields: [
                        {
                            type: 'text',
                            caption: 'Mobile Number',
                            name: 'mobileNumber',
                            id: 'mobileNumber',
                            placeholder: 'Enter mobile number',
                            required: true,
                            value: '',
                            keyboardFilter: 'pint',
                            maxLength: 10,
                            validationMessages: [
                                {
                                    type: 'phoneService',
                                    message: 'This phone number is invalid. Please double-check it.',
                                },
                            ],
                        },
                        {
                            type: 'button',
                            caption: 'Verify Phone',
                            name: 'verifyPhone',
                            buttonType: 'borderless',
                            imageSrc: '',
                        },
                    ],
                };

                this.modalService.confirm(popup, 'send-via-modal-above-pdf', insuredInformation).subscribe({
                    next: (popupCallbackDetail) => {
                        if (popupCallbackDetail?.[0] === 'Send') {
                            this.sendSMS(popupCallbackDetail[1]);
                        }
                    },
                    error: () => this.showToast('error', 'SMS Error', 'Unable to open SMS dialog. Please try again.'),
                });
            });
    }

    private sendSMS(mobileNumber: string | any) {
        const phone = typeof mobileNumber === 'object' ? mobileNumber?.mobileNumber : mobileNumber;
        if (!this.data.policyNo || !this.data.attachmentDocId || !this.data.subCategory) {
            this.showToast('error', '', 'Missing required document information.');
            return;
        }

        // Close the viewer immediately as the loader appears; the send continues in the background.
        this.dialogRef.close();

        this.phoneService
            .sendSMS(phone, this.data.policyNo, this.data.subCategory, this.data.attachmentDocId)
            .subscribe({
                next: () => this.showToast('success', '', `The ${this.data.documentCategory} was sent successfully.`),
                error: () => this.showToast('error', '', `The ${this.data.documentCategory} couldn't be sent.`),
            });
    }
}
